import { api } from "@/lib/api";
import {
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";

// ============================================================
// Auth Context — Login / Logout / User State
// ============================================================

interface User {
	id: string;
	email: string;
	name: string;
	role: string;
}

interface AuthState {
	user: User | null;
	token: string | null;
	isLoading: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [token, setToken] = useState<string | null>(() =>
		localStorage.getItem("token"),
	);
	const [isLoading, setIsLoading] = useState(true);

	// Beim Start: Token validieren
	useEffect(() => {
		if (!token) {
			setIsLoading(false);
			return;
		}

		api
			.get<{ data: User }>("/auth/me")
			.then((res) => setUser(res.data))
			.catch(() => {
				localStorage.removeItem("token");
				setToken(null);
			})
			.finally(() => setIsLoading(false));
	}, [token]);

	async function login(email: string, password: string) {
		const res = await api.post<{ data: { token: string; user: User } }>(
			"/auth/login",
			{ email, password },
		);
		localStorage.setItem("token", res.data.token);
		setToken(res.data.token);
		setUser(res.data.user);
	}

	function logout() {
		localStorage.removeItem("token");
		setToken(null);
		setUser(null);
	}

	return (
		<AuthContext.Provider value={{ user, token, isLoading, login, logout }}>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth(): AuthState {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within AuthProvider");
	return ctx;
}
